import { supabase } from "@/lib/supabase";
import type { BadgeKind, MemberBadge } from "@/lib/badges";
import type { IBadgeService } from "@/services/interfaces/IBadgeService";

interface BadgeRow {
  kind: BadgeKind;
  earned_at: string;
  organization: { id: string; slug: string; name: string } | null;
}

export class SupabaseBadgeService implements IBadgeService {
  async claim(kind: BadgeKind, orgId?: string): Promise<boolean> {
    const { data, error } = await supabase.rpc("claim_member_badge", {
      p_kind: kind,
      p_org: orgId ?? null,
    });
    if (error) throw new Error(error.message);
    return data === true;
  }

  // member_badges is readable by every signed-in member; organizations by
  // everyone (named columns only, 20261007100000).
  async listForUser(userId: string): Promise<MemberBadge[]> {
    const { data, error } = await supabase
      .from("member_badges")
      .select("kind, earned_at, organization:organizations(id, slug, name)")
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return ((data ?? []) as unknown as BadgeRow[])
      // An org badge whose org didn't embed has nothing to show or link to.
      .filter((r) => r.kind === "ready" || r.organization)
      .map((r) => ({ kind: r.kind, org: r.organization, earnedAt: r.earned_at }));
  }

  async readyForHandle(handle: string, profileId: string): Promise<string | null> {
    const { data, error } = await supabase.rpc("get_ready_badge_for_handle", {
      p_handle: handle,
      p_profile_id: profileId,
    });
    if (error) throw new Error(error.message);
    return (data as string | null) ?? null;
  }
}
